import React, { useState, useImperativeHandle, forwardRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Stack,
  TextField,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import { showMessage } from "../../../customhooks/useNotifier";
import knowledgebaseAgentService from "../../../services/knowledgebase-agent.service";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

/* ---------------- PLAN CONFIG ---------------- */
const PLAN_LIMITS = {
  free: 10,
  professional: 50,
  enterprise: Infinity,
};

// PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const DocumentUploadStep = forwardRef(({ formData }, ref) => {
  const [uploading, setUploading] = useState(false);
  const [localFiles, setLocalFiles] = useState([]);
  const [selectedPages, setSelectedPages] = useState({});
  const [previewVisible, setPreviewVisible] = useState(null);
  const [selectionError, setSelectionError] = useState("");

  /* ---------------- USER & PLAN ---------------- */
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userPlan = user?.plan || "free";
  const maxSelectable = PLAN_LIMITS[userPlan] ?? 10;

  useImperativeHandle(ref, () => ({
    validateStep: () => true,
  }));

  /* ---------------- HELPERS ---------------- */

  const totalSelectedPages = Object.values(selectedPages).reduce(
    (sum, pages) => sum + pages.length,
    0
  );

  const hasSelectedPages = totalSelectedPages > 0;

  /* ---------------- FILE HANDLING ---------------- */

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (localFiles.length + files.length > 5) {
      showMessage("Maximum 5 PDFs allowed", "error");
      return;
    }

    const newFiles = files.filter(
      (f) =>
        !localFiles.some((e) => e.name.toLowerCase() === f.name.toLowerCase())
    );

    if (!newFiles.length) {
      showMessage("Duplicate files skipped", "warning");
      return;
    }

    const mapped = newFiles.map((f) => ({ name: f.name, file: f }));
    setLocalFiles((p) => [...p, ...mapped]);

    mapped.forEach((f) => setSelectedPages((p) => ({ ...p, [f.name]: [] })));

    e.target.value = null;
  };

  const removeFile = (index) => {
    const removed = localFiles[index];
    setLocalFiles((p) => p.filter((_, i) => i !== index));

    setSelectedPages((p) => {
      const copy = { ...p };
      delete copy[removed.name];
      return copy;
    });

    if (previewVisible === removed.name) setPreviewVisible(null);
  };

  /* ---------------- PAGE SELECTION ---------------- */

  const togglePageSelection = (fileName, pageIndex) => {
    setSelectedPages((prev) => {
      const pages = prev[fileName] || [];
      const isSelected = pages.includes(pageIndex);

      // Remove page
      if (isSelected) {
        setSelectionError("");
        return {
          ...prev,
          [fileName]: pages.filter((p) => p !== pageIndex),
        };
      }

      // Limit reached
      if (userPlan !== "enterprise" && totalSelectedPages >= maxSelectable) {
        setSelectionError(
          `Your ${userPlan} plan allows selecting up to ${maxSelectable} pages`
        );
        return prev;
      }

      setSelectionError("");
      return {
        ...prev,
        [fileName]: [...pages, pageIndex],
      };
    });
  };

  /* ---------------- UPLOAD ---------------- */

  const uploadFiles = async () => {
    if (!hasSelectedPages) {
      showMessage("Select at least one page", "error");
      return;
    }

    try {
      setUploading(true);
      const fd = new FormData();
      const pagesMap = {};

      localFiles.forEach((f) => {
        if (selectedPages[f.name]?.length) {
          pagesMap[f.name] = selectedPages[f.name];
          fd.append("files", f.file);
        }
      });

      fd.append("kbId", formData.id);
      fd.append("language", formData.language || "en");
      fd.append("pagesMap", JSON.stringify(pagesMap));

      await knowledgebaseAgentService.uploadDocuments(fd);

      setLocalFiles([]);
      setSelectedPages({});
      setPreviewVisible(null);
      setSelectionError("");
    } catch (e) {
      showMessage("Upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  /* ---------------- PDF PREVIEW ---------------- */

  const DocumentPreviewer = ({ file, fileName }) => {
    const [numPages, setNumPages] = useState(0);
    const [page, setPage] = useState(1);
    const [range, setRange] = useState({ from: "", to: "" });

    const pageIndex = page - 1;
    const selected = selectedPages[fileName] || [];

    const selectAll = () => {
      setSelectedPages((p) => {
        const current = p[fileName]?.length || 0;

        if (userPlan !== "enterprise") {
          const remaining = maxSelectable - (totalSelectedPages - current);
          if (remaining <= 0) {
            setSelectionError(
              `Your ${userPlan} plan allows selecting up to ${maxSelectable} pages`
            );
            return p;
          }

          return {
            ...p,
            [fileName]: Array.from(
              { length: Math.min(numPages, remaining) },
              (_, i) => i
            ),
          };
        }

        return {
          ...p,
          [fileName]: Array.from({ length: numPages }, (_, i) => i),
        };
      });
    };

    const clearAll = () => setSelectedPages((p) => ({ ...p, [fileName]: [] }));

    const applyRange = () => {
      const f = Number(range.from) - 1;
      const t = Number(range.to) - 1;
      if (f >= 0 && t < numPages && f <= t) {
        setSelectedPages((p) => {
          const pages = [];
          for (let i = f; i <= t; i++) {
            if (
              userPlan !== "enterprise" &&
              totalSelectedPages + pages.length >= maxSelectable
            )
              break;
            pages.push(i);
          }
          return { ...p, [fileName]: pages };
        });
      }
    };

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
          Selected Pages: {totalSelectedPages}
          {userPlan !== "enterprise" && ` / ${maxSelectable}`}
        </Typography>

        {selectionError && (
          <Typography variant="caption" color="error">
            {selectionError}
          </Typography>
        )}

        <Document
          file={file}
          onLoadSuccess={({ numPages }) => {
            setNumPages(numPages);
            setPage(1);
          }}
        >
          <Page pageNumber={page} width={520} renderTextLayer={false} />
        </Document>

        <Stack direction="row" spacing={1} justifyContent="center" mt={1}>
          <Button
            size="small"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </Button>
          <Typography variant="caption">
            Page {page} / {numPages}
          </Typography>
          <Button
            size="small"
            disabled={page === numPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Stack>

        <Button
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          disabled={
            !selected.includes(pageIndex) &&
            userPlan !== "enterprise" &&
            totalSelectedPages >= maxSelectable
          }
          variant={selected.includes(pageIndex) ? "contained" : "outlined"}
          onClick={() => togglePageSelection(fileName, pageIndex)}
        >
          {selected.includes(pageIndex) ? "Page Selected ✓" : "Select Page"}
        </Button>

        <Stack direction="row" spacing={1} mt={1}>
          <Button size="small" onClick={selectAll}>
            Select All
          </Button>
          <Button size="small" onClick={clearAll}>
            Clear All
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} mt={1}>
          <TextField
            size="small"
            label="From"
            value={range.from}
            onChange={(e) => setRange({ ...range, from: e.target.value })}
          />
          <TextField
            size="small"
            label="To"
            value={range.to}
            onChange={(e) => setRange({ ...range, to: e.target.value })}
          />
          <Button size="small" onClick={applyRange}>
            Apply
          </Button>
        </Stack>

        {selected.length > 0 && (
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
            {selected.map((p) => (
              <Chip
                key={p}
                label={`Page ${p + 1}`}
                onDelete={() => togglePageSelection(fileName, p)}
                size="small"
              />
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  /* ---------------- UI ---------------- */

  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Upload Documents 📄
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload manuals, FAQs, brochures, or product sheets.
        </Typography>
      </Box>

      <Card sx={{ mb: 4, border: "1px solid rgba(0,0,0,0.08)" }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Add Documents (Optional)
          </Typography>

          <input
            type="file"
            hidden
            multiple
            accept=".pdf"
            id="document-file-input"
            onChange={handleFileSelect}
          />

          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() =>
              document.getElementById("document-file-input")?.click()
            }
          >
            Select PDFs
          </Button>

          {localFiles.map((file, i) => (
            <Card key={i} sx={{ mt: 2, p: 1.5 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography>{file.name}</Typography>
                <Box>
                  <Button size="small" onClick={() => removeFile(i)}>
                    Remove
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      setPreviewVisible(
                        previewVisible === file.name ? null : file.name
                      )
                    }
                  >
                    Preview
                  </Button>
                </Box>
              </Box>

              {previewVisible === file.name && (
                <DocumentPreviewer file={file.file} fileName={file.name} />
              )}
            </Card>
          ))}

          {localFiles.length > 0 && (
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              disabled={uploading || !hasSelectedPages}
              onClick={uploadFiles}
            >
              Upload Files
            </Button>
          )}

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption">Uploading documents…</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
});

export default DocumentUploadStep;
